import { useCallback, useDeferredValue, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import { v4 as uuidv4 } from 'uuid';

import {
  GridActionsCellItem,
  GridEventListener,
  GridRenderCellParams,
  GridRowEditStopReasons,
  GridRowId,
  GridRowModes,
  GridRowModesModel,
  GridColDef,
  GridRowParams,
  GridCellParams,
  gridClasses,
  useGridApiRef,
  useGridApiContext,
  GridRenderEditCellParams,
  GridRowClassNameParams,
  GridValueGetter,
  GridActionsCellItemProps,
  GridRowSelectionModel,
} from '@mui/x-data-grid-premium';
import RawDataGrid from '../molecules/DataGrid';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SaveIcon from '@mui/icons-material/Save';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import CancelIcon from '@mui/icons-material/Close';
import { ILabels } from '@bloomays-lib/types.shared';
import { Button, LoaderPending, Checkbox, Slider } from '@bloomays-lib/ui.shared';
import { FreeSoloAutoComplete } from '../atoms/FreeSoloAutoComplete';
import { IFuseOptions } from 'fuse.js';
import useFuse from '../../customHooks/useFuse';
import { notify } from '../../helpers/toastify';
import { cleanEditableLabelBeforeCreate, cleanEditableLabelBeforeUpdate } from '../../helpers/label-management';
const DEFAULT_MERGE_VALUE = 'choose_iso';

type ILabelEditable<T extends ILabels> = T & {
  isNew?: boolean;
  merge?: string;
};

export type LabelManagmentProps<T extends ILabels> = {
  labels: T[];
  onError: (error: any) => void;
  onUpdate: (newRow: T, oldRow: T) => Promise<T | undefined>;
  onCreate?: (newRow: T) => Promise<T | undefined>;
  onFlat?: (labelId: string) => Promise<T[] | undefined>;
  onDelete: (labelId: string) => Promise<T | undefined>;
  onMerge: (mergeToLabelId: string, mergeInLabelId: string) => Promise<T | undefined>;
  onDeleteUnusedLabels?: () => Promise<number>;
};

const DataGrid = styled(RawDataGrid)(({ theme }) => ({
  [`& .${gridClasses.row}.even`]: {
    backgroundColor: theme.palette.neutral[10],
  },
}));

const LabelManagment = <T extends ILabels>({
  labels,
  onError,
  onUpdate,
  onDelete,
  onMerge,
  onCreate,
  onFlat,
  onDeleteUnusedLabels,
}: LabelManagmentProps<T>) => {
  const [rows, setRows] = useState<ILabelEditable<T>[]>(labels);
  const [pending, setPending] = useState<boolean>(false);
  const [isoRows, setIsoRows] = useState<ILabelEditable<T>[]>(labels.filter((s) => s.iso));
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});

  // ✅ Migration v8 : nouveau modèle de sélection
  const [rowsSelected, setRowsSelected] = useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set(),
  });
  const [rowsSelectedLabel, setRowsSelectedLabel] = useState<string>();
  const resolveRows = useCallback(() => Promise.resolve(isoRows), [isoRows]);
  const [sliderValue, setSliderValue] = useState<number>(0.2);
  const [fuseThreshold, setFuseThreshold] = useState<number>(0.2);
  const [fuseOptions, setFuseOptions] = useState<IFuseOptions<ILabelEditable<T>>>({});
  const deferredFuseThreshold = useDeferredValue(fuseThreshold);

  const apiRef = useGridApiRef();

  useEffect(() => {
    const handleEvent: GridEventListener<'rowSelectionChange'> = (
      params, // GridRowSelectionModel
      event, // MuiEvent<React.ChangeEvent<HTMLElement>>
      details, // GridCallbackDetails
    ) => {
      // ✅ Migration v8 : utiliser le nouveau modèle de sélection
      setRowsSelected(params);
    };

    // The `subscribeEvent` method will automatically unsubscribe in the cleanup function of the `useEffect`.
    return apiRef.current?.subscribeEvent('rowSelectionChange', handleEvent);
  }, [apiRef]);

  useEffect(() => {
    const initRowModel: GridRowModesModel = {};
    labels.forEach((t) => {
      if (t.id) {
        initRowModel[t.id] = { mode: GridRowModes.Edit };
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setFuseOptions({
      keys: ['label', 'alternateLabels'],
      threshold: deferredFuseThreshold,
      shouldSort: true,
    });
  }, [deferredFuseThreshold]);

  const searchFuse = useFuse<ILabelEditable<T>>(isoRows, fuseOptions);

  const EditToolbar = useCallback(() => {
    const handleClick = () => {
      const id = uuidv4(); // ⇨ '9b1deb4d-3b7d-4bad-9bdd-2b0d7b3dcb6d'
      setRows((oldRows) => [
        {
          id,
          label: '',
          alternateLabels: [],
          iso: false,
          isNew: true,
        } as unknown as any,
        ...oldRows,
      ]);
      setRowModesModel((oldModel) => ({
        ...oldModel,
        [id]: { mode: GridRowModes.Edit, fieldToFocus: 'label' },
      }));
    };

    return onCreate && <Button key={'customHeaderButton'} onClick={handleClick} textButton="Add record"></Button>;
  }, [onCreate]);

  useEffect(() => {
    setIsoRows(rows.filter((s) => s.iso));
  }, [setIsoRows, rows]);

  const setPendingNow = (pendingState: boolean) => {
    setTimeout(() => {
      setPending(pendingState);
    }, 0);
  };

  const handleRowEditStop: GridEventListener<'rowEditStop'> = (params: any, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel((prev) => ({ ...prev, [id]: { mode: GridRowModes.Edit } }));
  };

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel((prev) => ({ ...prev, [id]: { mode: GridRowModes.View } }));
  };

  const handleFlatClick = (id: GridRowId) => async () => {
    setPendingNow(true);
    if (onFlat === undefined) {
      return;
    }
    const flatedLabels = await onFlat(id.toString());
    setPendingNow(false);
    const newRows =
      flatedLabels?.length && flatedLabels?.length > 0
        ? rows.filter((row) => row.id.toString() !== id.toString())
        : rows;
    if (flatedLabels) {
      flatedLabels.forEach((flatedLabel) => {
        // un flat peu renvoyer une row deja existante, ne pas la rajouter
        if (!newRows.find((row) => row.id === flatedLabel.id)) {
          newRows.push({ ...flatedLabel, isNew: false });
        }
      });
    }

    setRows(newRows);
  };

  const handleDeleteClick = (id: GridRowId) => async () => {
    setPendingNow(true);
    const deletedLabel = await onDelete(id.toString());
    setPendingNow(false);
    if (deletedLabel) {
      setRows(rows.filter((row) => row.id.toString() !== deletedLabel.id));
    }
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel((prev) => ({
      ...prev,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    }));

    const editedRow = rows.find((row) => row.id === id);
    if (editedRow?.isNew) {
      setRows(rows.filter((row) => row.id !== id));
    }
  };

  const processRowUpdate = async (newRow: ILabelEditable<T>, originalRow: T) => {
    setPendingNow(true);
    if (newRow.merge && newRow.merge !== DEFAULT_MERGE_VALUE && newRow.merge !== '') {
      const findnewRow = rows.find((row) => row.label === newRow.merge && row.iso === true);
      if (!findnewRow) {
        throw new Error(`Can't find iso label ${newRow.merge}`);
      }
      return onMerge(originalRow.id, findnewRow.id).then((s) => {
        setRows(rows.filter((row) => row.id.toString() !== originalRow.id));
        setPendingNow(false);
        return s;
      });
    }

    if (newRow.isNew && onCreate) {
      const serverNewRow = await onCreate(cleanEditableLabelBeforeCreate(newRow));
      setPendingNow(false);
      if (serverNewRow) {
        setRows(rows.map((row) => (row.id === newRow.id ? { ...newRow, id: serverNewRow.id, isNew: false } : row)));
      }
      return serverNewRow;
    }

    setRows(rows.map((row) => (row.id === newRow.id ? newRow : row)));
    const cleanedNewRow = cleanEditableLabelBeforeUpdate(newRow);
    const cleanedOriginalRow = cleanEditableLabelBeforeUpdate(originalRow);
    const resUp = await onUpdate(cleanedNewRow, cleanedOriginalRow);
    setPendingNow(false);
    return resUp;
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const selectedRowUpdate = useCallback(() => {
    const selectedIds = Array.from(rowsSelected.ids);
    setRows(
      rows.map((row) => {
        if (selectedIds.includes(row.id as string)) {
          return { ...row, merge: rowsSelectedLabel };
        }
        return row;
      }),
    );
    // turn row on edit mode
    const editableRows: GridRowModesModel = {};
    selectedIds.forEach((rowId) => {
      if (rows.find((r) => r.id === rowId)) {
        editableRows[rowId] = { mode: GridRowModes.Edit };
      }
    });
    setRowModesModel((prev) => ({ ...rowModesModel, ...editableRows }));
  }, [rows, rowsSelected, rowModesModel, rowsSelectedLabel]);

  const handleSliderChange = useCallback(
    (event: Event, value: number | number[]) => {
      setSliderValue(Array.isArray(value) ? value?.[0] || 0 : value);
    },
    [setSliderValue],
  );

  const handleSliderCommited = useCallback(
    (event: React.SyntheticEvent | Event, value: number | number[]) => {
      setFuseThreshold(Array.isArray(value) ? value?.[0] || 0 : value);
    },
    [setFuseThreshold],
  );

  const setTagFromFreeSolo = useCallback(
    (tags: string[]) => {
      const label = tags?.[0] ?? undefined;
      setRowsSelectedLabel(label);
    },
    [setRowsSelectedLabel],
  );

  const SliderFuse = useCallback(() => {
    const sliderMarks = [
      {
        value: 0,
        label: '0',
      },
      {
        value: 1,
        label: '1',
      },
    ];
    const selectedCount = rowsSelected.ids.size;
    return (
      <>
        {selectedCount > 0 && (
          <>
            <FreeSoloAutoComplete
              key={'customHeaderFreeSolo'}
              tags={rowsSelectedLabel ? [rowsSelectedLabel] : []}
              loading={false}
              multiple={false}
              freeSolo={false}
              setTag={setTagFromFreeSolo}
              collectTags={resolveRows}
              label={''}
              placeholder={''}
              initTags={isoRows}
            />
            <Button disable={rowsSelectedLabel === undefined} textButton={'Appliquer'} onClick={selectedRowUpdate} />
          </>
        )}
        <Slider
          key={'customHeaderSlider'}
          value={sliderValue}
          defaultValue={sliderValue}
          label={'Sensibilité matching'}
          min={0.0}
          max={1}
          onChange={handleSliderChange}
          onChangeCommitted={handleSliderCommited}
          step={0.01}
          marks={sliderMarks}
          sx={{ width: 150, padding: '4px 20px 0px 10px' }}
        />
      </>
    );
  }, [
    handleSliderChange,
    handleSliderCommited,
    isoRows,
    resolveRows,
    rowsSelected.ids.size,
    rowsSelectedLabel,
    selectedRowUpdate,
    setTagFromFreeSolo,
    sliderValue,
  ]);

  function CustomEditAutoComplete(props: GridRenderEditCellParams<ILabelEditable<T>>) {
    const { id, value, field, hasFocus } = props;
    const apiRef = useGridApiContext();
    const ref = useRef<HTMLInputElement>(null);

    const setTag = useCallback(
      (tags: string[]) => {
        const jobTitle = tags?.[0] ?? undefined;
        apiRef.current?.setEditCellValue({
          id,
          field,
          value: jobTitle,
          debounceMs: 200,
        });
      },
      [apiRef, field, id],
    );

    useLayoutEffect(() => {
      if (hasFocus) {
        (ref.current as any)?.focus();
      }
    }, [hasFocus]);
    return (
      <FreeSoloAutoComplete
        tags={value ? [value] : []}
        loading={false}
        multiple={false}
        freeSolo={false}
        setTag={setTag}
        collectTags={resolveRows}
        label={''}
        placeholder={''}
        initTags={isoRows}
      />
    ) as React.ReactNode;
  }

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'Id',
      sortable: true,
      editable: true,
      minWidth: 300,
      headerClassName: 'header',
    },
    {
      field: 'label',
      headerName: 'Label',
      sortable: true,
      editable: true,
      minWidth: 300,
      headerClassName: 'header',
    },
    {
      field: 'iso',
      headerName: 'Normalisé (iso)',
      headerClassName: 'header',
      sortable: false,
      groupable: false,
      filterable: true,
      minWidth: 150,
      align: 'center',
      editable: true,
      type: 'boolean',
      renderCell: (params: GridRenderCellParams<T>) => {
        return (<Checkbox name={'iso'} checked={params.row.iso} disabled={true} />) as React.ReactNode;
      },
    },
    {
      field: 'alternateLabels',
      headerName: 'Labels Alternatifs',
      headerClassName: 'header',
      sortable: false,
      filterable: false,
      editable: true,
      minWidth: 300,
      valueGetter: (value: GridValueGetter<T>, row: T) => {
        if (row.alternateLabels && row.alternateLabels.join) {
          return row.alternateLabels?.join(',') || '';
        }
      },
      valueParser: (value: string) => {
        return value?.split(',') || [];
      },
    },
    {
      field: 'merge',
      headerName: 'Fusionner avec...',
      headerClassName: 'header',
      editable: true,
      sortable: false,
      filterable: false,
      minWidth: 400,
      type: 'string',
      width: 130,
      renderEditCell: (params: GridRenderEditCellParams<ILabelEditable<T>>) => {
        return (<CustomEditAutoComplete {...params} />) as React.ReactNode;
      },
      valueGetter: (value: GridValueGetter<ILabelEditable<T>>, row: ILabelEditable<T>) => {
        const label = row.label;
        if (!label || row.iso === true) {
          return undefined;
        }

        if (value) {
          return value;
        }
        const search = searchFuse(label);
        return search?.[0]?.item.label || undefined;
      },
    },
    {
      field: 'actions',
      type: 'actions',
      sortable: false,
      filterable: false,
      width: 150,
      headerName: 'Actions',
      headerClassName: 'header',
      getActions: (params: GridRowParams<ILabelEditable<T>>) => {
        const isInEditMode = rowModesModel[params.id]?.mode === GridRowModes.Edit;
        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon sx={{ color: 'primary.main' }} />}
              label="Save"
              role="button"
              aria-label="Save"
              onClick={handleSaveClick(params.id)}
            />,
            <GridActionsCellItem
              icon={<AltRouteIcon sx={{ color: 'primary.main' }} />}
              label="Split"
              role="button"
              disabled={onFlat === undefined}
              aria-label="Split"
              onClick={handleFlatClick(params.id)}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              role="button"
              aria-label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(params.id)}
              color="inherit"
            />,
          ] as React.ReactElement<GridActionsCellItemProps>[];
        }

        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            role="button"
            aria-label="Edit"
            className="textPrimary"
            onClick={handleEditClick(params.id)}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            disabled={params.row.iso}
            label="Delete"
            role="button"
            aria-label="Delete"
            onClick={handleDeleteClick(params.id)}
            color="inherit"
          />,
        ] as React.ReactElement<GridActionsCellItemProps>[];
      },
    },
  ];
  return (
    <div>
      <LoaderPending text="Mise à jour en cours, veuillez patienter ..." open={pending} />
      {labels && labels.length > 0 && (
        <DataGrid
          uniqueDatagridId="label-managment"
          apiRef={apiRef}
          childrenToolBar={
            <>
              {SliderFuse()}
              {EditToolbar()}
            </>
          }
          isCellEditable={(params: GridCellParams<T>) => {
            if (params.field === 'merge' && params.row.iso) {
              return false;
            }
            return true;
          }}
          pagination={true}
          getRowClassName={(params: GridRowClassNameParams) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
          }
          hideFooterPagination={false}
          rows={rows}
          getRowId={(row: T) => {
            return row.id;
          }}
          columns={columns}
          pageSize={50}
          sx={{
            width: '100%',
          }}
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={onError}
          editMode="row"
          rowModesModel={rowModesModel}
          onRowModesModelChange={handleRowModesModelChange}
          onRowEditStop={handleRowEditStop}
          checkboxSelection
        />
      )}
      {onDeleteUnusedLabels && (
        <>
          <br />
          <br />
          <Button
            textButton="Supprimer les items non utiliséss"
            onClick={async () => {
              const deleted = await onDeleteUnusedLabels();
              notify('success', `${deleted || 0} items suppriméss`, null, {
                hideProgressBar: true,
                autoClose: 2000,
              });
            }}
          />
        </>
      )}
    </div>
  ) as React.ReactNode;
};
export { LabelManagment };
export default LabelManagment;
